import React, { memo, useCallback, useEffect, useMemo, useState } from "react";
import PartnerDetailsView from "./partner-details-view";
import EditPartnerModal from "./edit-partner-modal";
import { Iconography, IconographySizes } from "@hid-galaxy-ui/galaxy-react";
import { useAppSelector } from "../../../../../../redux";
import parsePhoneNumberFromString from "libphonenumber-js";
import { getValidDateOrNull } from "../../../../../../utils/getValidDateOrNull";
import { Constants } from "../../../../../../utils/constants";
import { usePartnerTabPermission } from "../../../../../../contexts/PartnerPermissionContext";
import { useSessionCanEditModule } from "../../../../../../hooks/useSessionPermissions";
import { PARTNER_MANAGEMENT_PERMISSION_KEYS as PM_KEYS } from "../../../../../../utils/permissions/partnerManagementPermissionKeys";
import { selectPartnerDetails } from "../../../../../../redux/slices/partner-details/selectors";
import { PartnerFormData } from "../../../../../../hooks/usePartnerForm";
import "./PartnerDetails.scss";

const PartnerDetails = memo(() => {
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [partnerDetails, setPartnerDetails] = useState<PartnerFormData>({
    name: "",
    email: "",
    accountManagerEmail: "",
    type: [],
    potential: "",
    website: "",
    description: "",
    region: "",
    revenueStream: "",
    partnerVertical: [],
    partnerTier: "",
    country: "",
    state: "",
    countryCode: "",
    phoneNumber: "",
    streetAddress: "",
    registrationDate: null,
  });

  const partnerDetailsInitial = useAppSelector(selectPartnerDetails);
  const { readOnly: tabReadOnly } = usePartnerTabPermission();
  const canEditPartnerConfig = useSessionCanEditModule(
    PM_KEYS.PARTNER_CONFIGURATIONS
  );
  const hasEditPermission = canEditPartnerConfig && !tabReadOnly;

  const isPartnerInactive = useMemo(
    () => partnerDetailsInitial?.status === "Inactive",
    [partnerDetailsInitial?.status]
  );

  const EditPartnerPermission = useMemo(
    () => hasEditPermission && !isPartnerInactive,
    [hasEditPermission, isPartnerInactive]
  );

  const parsedPhoneData = useMemo(() => {
    if (!partnerDetailsInitial?.company_contact) {
      return { dialCode: "+1", nationalNo: "" };
    }

    const parsedPhoneNumber = parsePhoneNumberFromString(
      partnerDetailsInitial.company_contact
    );
    if (parsedPhoneNumber) {
      return {
        dialCode: "+" + parsedPhoneNumber.countryCallingCode,
        nationalNo: parsedPhoneNumber.nationalNumber,
      };
    }
    return { dialCode: "+1", nationalNo: "" };
  }, [partnerDetailsInitial?.company_contact]);

  useEffect(() => {
    if (partnerDetailsInitial) {
      setPartnerDetails({
        name: partnerDetailsInitial.partner_name || "",
        email: partnerDetailsInitial.partner_email || "",
        accountManagerEmail: partnerDetailsInitial.account_manager || "",
        type: partnerDetailsInitial.partner_type || [],
        potential: partnerDetailsInitial.partner_potential || "",
        website: partnerDetailsInitial.website || "",
        description: partnerDetailsInitial.description || "",
        region: partnerDetailsInitial.region || Constants.REGIONS[0],
        revenueStream: partnerDetailsInitial.revenue_stream || "",
        partnerVertical: (
          partnerDetailsInitial.partner_vertical?.split(",") || []
        ).filter((v: string) => v.trim()),
        partnerTier: partnerDetailsInitial.partner_tier || "",
        country: partnerDetailsInitial.country || "",
        state: partnerDetailsInitial.state || "",
        countryCode: parsedPhoneData.dialCode,
        phoneNumber: parsedPhoneData.nationalNo,
        streetAddress: partnerDetailsInitial.street_address || "",
        registrationDate: getValidDateOrNull(
          partnerDetailsInitial.registration_date
        ),
      });
    }
  }, [partnerDetailsInitial, parsedPhoneData]);

  const handleEditClick = useCallback(() => {
    setIsEditModalOpen(true);
  }, []);

  const handleCloseEditModal = useCallback(() => {
    setIsEditModalOpen(false);
  }, []);

  if (!partnerDetailsInitial) {
    return <div>Loading...</div>;
  }

  return (
    <div className="partner-details">
      <div className="partner-details-header">
        <h2>Partner Details</h2>
        {!isPartnerInactive && (
          <button
            type="button"
            className="edit-link"
            onClick={handleEditClick}
            disabled={!EditPartnerPermission}
          >
            <Iconography icon="pen" size={IconographySizes.Small} />
            Edit
          </button>
        )}
      </div>

      <PartnerDetailsView
        partnerDetails={partnerDetails}
        partnerDetailsInitial={partnerDetailsInitial}
      />

      <EditPartnerModal open={isEditModalOpen} onClose={handleCloseEditModal} />
    </div>
  );
});

export default PartnerDetails;
